import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// F2-006: timeout máximo para esperar resolución del contexto de auth
const AUTH_TIMEOUT_MS = 10000;

interface PrivateRouteProps {
  children: React.ReactNode;
  roles: ('ADMIN' | 'ASOCIADO' | 'AFILIADO')[];
  permission?: string; // Acción específica a verificar
  resourceData?: any; // Datos del recurso para verificaciones específicas
  fallbackPath?: string;
  showError?: boolean;
  allowAdminComite?: boolean; // Permitir acceso a admins de comité
}

export default function PrivateRoute({
  children,
  roles,
  permission,
  resourceData,
  fallbackPath = "/",
  showError = false,
  allowAdminComite = false
}: PrivateRouteProps) {
  const { user, loading } = useUser();
  const { checkPermission } = usePermissions();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = setTimeout(() => setTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [loading]);

  // Si el contexto tardó demasiado, redirigir al login en vez de quedar colgado
  if (loading && timedOut) {
    return <Navigate to="/" replace state={{ reason: 'auth_timeout' }} />;
  }

  // Si está cargando, mostrar loading
  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Verificando permisos...</span>
          </div>
          <p className="mt-2">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si no hay usuario, redirige al home
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Verificar roles básicos
  const hasRequiredRole = roles.includes(user.rol as 'ADMIN' | 'ASOCIADO' | 'AFILIADO');

  // Verificar si es admin de comité y se permite acceso
  const isAdminComiteWithAccess = allowAdminComite && user.esAdminComite === true;

  if (!hasRequiredRole && !isAdminComiteWithAccess) {
    if (showError) {
      Swal.fire({
        title: '¡Ups! Acceso Restringido',
        html: `
          <p>Lo sentimos, pero no tienes los permisos necesarios para acceder a esta sección.</p>
          <div class="mt-3">
            <small class="text-muted">
              <strong>Roles requeridos:</strong> ${roles.join(', ')}<br>
              <strong>Tu rol actual:</strong> ${user.rol}
            </small>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#0d6efd',
        allowOutsideClick: false
      }).then(() => {
        window.history.back();
      });
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Verificar permisos específicos si se especifica una acción
  if (permission) {
    const permissionResult = checkPermission(permission, resourceData);
    
    if (!permissionResult.hasAccess) {
      if (showError) {
        Swal.fire({
          title: '¡Ops! Sin Acceso a este Recurso',
          html: `
            <p>No tienes permisos para acceder a este recurso específico.</p>
            <div class="mt-3">
              <small class="text-muted">${permissionResult.reason || 'Verifica que tengas los permisos necesarios.'}</small>
            </div>
          `,
          icon: 'warning',
          confirmButtonText: 'Regresar',
          confirmButtonColor: '#fd7e14',
          allowOutsideClick: false
        }).then(() => {
          window.history.back();
        });
      }
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
}